import React, { useMemo } from 'react';
import { BarChart3 } from 'lucide-react';

const HealthScoreViolin = ({ data }) => {
  const violinData = useMemo(() => {
    const entityTypes = {};
    
    data.forEach(item => {
      const type = item.entityType || 'UNKNOWN';
      if (!entityTypes[type]) {
        entityTypes[type] = [];
      }
      if (item.healthScore > 0) {
        entityTypes[type].push(item.healthScore);
      }
    });

    return Object.entries(entityTypes)
      .map(([type, scores]) => {
        scores.sort((a, b) => a - b);
        const count = scores.length;
        
        // Calculate statistics
        const min = scores[0] || 0;
        const max = scores[count - 1] || 0;
        const median = scores[Math.floor(count / 2)] || 0;
        const q1 = scores[Math.floor(count * 0.25)] || 0;
        const q3 = scores[Math.floor(count * 0.75)] || 0;
        const mean = scores.reduce((a, b) => a + b, 0) / count || 0;

        // Create distribution buckets
        const buckets = Array(10).fill(0);
        scores.forEach(score => {
          const bucket = Math.min(9, Math.floor(score / 10));
          buckets[bucket]++;
        });

        return {
          type,
          count,
          min,
          max,
          median,
          q1,
          q3,
          mean: Math.round(mean),
          buckets,
          maxBucket: Math.max(...buckets)
        };
      })
      .filter(d => d.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [data]);

  const getColorForScore = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#eab308';
    if (score >= 30) return '#f97316';
    return '#ef4444';
  };

  return (
    <div className="bg-dark-card rounded-lg border border-dark-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 size={24} className="text-cyan-accent" />
        <h3 className="text-xl font-bold text-cyan-accent">Health Score Distribution</h3>
        <span className="text-sm text-gray-500">(Top 6 entity types)</span>
      </div>

      <div className="space-y-6">
        {violinData.map((violin, idx) => (
          <div key={violin.type} className="bg-dark-border/20 rounded-lg p-4 border border-dark-border/60">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-bold text-white">{violin.type}</div>
                <div className="text-xs text-gray-500">{violin.count} services</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-cyan-accent">{violin.mean}</div>
                <div className="text-xs text-gray-400">Mean Score</div>
              </div>
            </div>

            {/* Violin Plot */}
            <div className="relative h-32 flex items-center">
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-500 pr-2">
                <span>100</span>
                <span>50</span>
                <span>0</span>
              </div>

              {/* Plot area */}
              <div className="flex-1 ml-8 relative h-full">
                {/* Grid lines */}
                <div className="absolute inset-0 flex flex-col justify-between">
                  {[0, 1, 2, 3, 4].map(i => (
                    <div key={i} className="border-t border-dark-border/30"></div>
                  ))}
                </div>

                {/* Violin shape */}
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full flex justify-around items-end h-full py-2">
                    {violin.buckets.map((count, bucketIdx) => {
                      const score = (bucketIdx + 0.5) * 10;
                      const height = (count / violin.maxBucket) * 100;
                      const yPosition = 100 - score;
                      
                      return (
                        <div
                          key={bucketIdx}
                          className="relative flex-1 mx-0.5"
                          style={{ height: '100%' }}
                        >
                          <div
                            className="absolute left-0 right-0 rounded transition-all hover:opacity-80"
                            style={{
                              top: `${yPosition}%`,
                              height: '10%',
                              backgroundColor: getColorForScore(score),
                              opacity: count > 0 ? 0.6 : 0.1,
                              transform: `scaleX(${height / 100})`,
                              boxShadow: count > 0 ? `0 0 10px ${getColorForScore(score)}40` : 'none'
                            }}
                            title={`${score.toFixed(0)}: ${count} services`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Box plot overlay */}
                <div className="absolute inset-0 flex items-center pointer-events-none">
                  <div className="w-full relative" style={{ height: '100%' }}>
                    {/* Median line */}
                    <div
                      className="absolute left-0 right-0 border-t-2 border-cyan-accent"
                      style={{
                        top: `${100 - violin.median}%`,
                        boxShadow: '0 0 10px #00e5ff'
                      }}
                    />
                    {/* Q1-Q3 box */}
                    <div
                      className="absolute left-1/4 right-1/4 border-2 border-purple-accent/60 bg-purple-accent/10 rounded"
                      style={{
                        top: `${100 - violin.q3}%`,
                        height: `${violin.q3 - violin.q1}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="mt-4 grid grid-cols-5 gap-2 text-xs">
              <div className="text-center">
                <div className="font-bold text-white">{violin.min}</div>
                <div className="text-gray-500">Min</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-purple-accent">{violin.q1}</div>
                <div className="text-gray-500">Q1</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-cyan-accent">{violin.median}</div>
                <div className="text-gray-500">Median</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-purple-accent">{violin.q3}</div>
                <div className="text-gray-500">Q3</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-white">{violin.max}</div>
                <div className="text-gray-500">Max</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-dark-border/50 flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-8 h-0.5 bg-cyan-accent"></div>
          <span className="text-gray-400">Median</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-4 border-2 border-purple-accent/60 bg-purple-accent/10 rounded"></div>
          <span className="text-gray-400">Q1-Q3 Range</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-lime-accent"></div>
          <span className="text-gray-400">Distribution</span>
        </div>
      </div>
    </div>
  );
};

export default HealthScoreViolin;
